import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';

/**
 * Injects admin-configured tracking scripts (GA4 / Meta Pixel / LinkedIn
 * Insight / GTM + free-form ``<head>`` / ``<body-end>`` HTML) into the DOM.
 *
 * Runs once at app mount; re-evaluates on route change so per-page scoping
 * works. Uses ``data-blayzrr-tag="<id>"`` on every injected element so we
 * can identify and remove them when the config changes or the current
 * route stops matching.
 *
 * Design notes
 * ------------
 * * We inject *scripts* via ``document.createElement('script')`` (not
 *   ``innerHTML``) — some browsers won't execute scripts inserted via
 *   innerHTML. For inline snippets in ``custom_head_html`` we parse them
 *   with DOMParser and re-create each ``<script>`` with proper attributes.
 * * Named-ID snippets are generated in JS so we don't need to store the
 *   full boilerplate in the DB — just the ID.
 * * All injections are idempotent — re-running the effect swaps the tags
 *   in place rather than duplicating.
 */

const MARKER = 'data-blayzrr-tag';

function tagEl(el, id) {
  try { el.setAttribute(MARKER, id); } catch (_) { /* ignore */ }
  return el;
}

function removeAllInjected() {
  document.querySelectorAll(`[${MARKER}]`).forEach((n) => {
    try { n.parentNode.removeChild(n); } catch (_) { /* ignore */ }
  });
}

// ---- Snippet generators (per named ID) ------------------------------------

function ga4Snippet(id) {
  if (!id) return null;
  const gtag = document.createElement('script');
  gtag.async = true;
  gtag.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  const init = document.createElement('script');
  init.textContent = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${id.replace(/'/g, '')}');`;
  return [tagEl(gtag, 'ga4-loader'), tagEl(init, 'ga4-init')];
}

function metaSnippet(id) {
  if (!id) return null;
  const s = document.createElement('script');
  s.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${id.replace(/'/g, '')}');fbq('track','PageView');`;
  return [tagEl(s, 'meta-pixel')];
}

function linkedinSnippet(id) {
  if (!id) return null;
  const s1 = document.createElement('script');
  s1.textContent = `_linkedin_partner_id="${id.replace(/"/g, '')}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);`;
  const s2 = document.createElement('script');
  s2.async = true;
  s2.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  return [tagEl(s1, 'li-insight-init'), tagEl(s2, 'li-insight-loader')];
}

function gtmSnippet(id) {
  if (!id) return null;
  const s = document.createElement('script');
  s.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id.replace(/'/g, '')}');`;
  return [tagEl(s, 'gtm-loader')];
}

// Parse raw HTML into elements + turn any embedded ``<script>`` into a real
// executable element (innerHTML-inserted scripts don't run).
function parseAndBuild(rawHtml, tagId) {
  if (!rawHtml || !rawHtml.trim()) return [];
  const template = document.createElement('template');
  template.innerHTML = rawHtml;
  const out = [];
  template.content.childNodes.forEach((n) => {
    if (n.nodeType === 1 && n.tagName === 'SCRIPT') {
      const s = document.createElement('script');
      for (const attr of n.attributes) s.setAttribute(attr.name, attr.value);
      if (n.textContent) s.textContent = n.textContent;
      out.push(tagEl(s, tagId));
    } else if (n.nodeType === 1) {
      out.push(tagEl(n.cloneNode(true), tagId));
    } else if (n.nodeType === 3 && n.textContent.trim()) {
      // Text nodes wrapped in a comment container to preserve them safely.
      const c = document.createComment(n.textContent);
      out.push(c);
    }
  });
  return out;
}

function currentPathMatches(cfg, path) {
  if (!cfg || cfg.scope !== 'pages') return true;
  const list = Array.isArray(cfg.pages) ? cfg.pages : [];
  if (list.length === 0) return false;
  return list.some((p) => {
    if (!p) return false;
    if (p === '/') return path === '/';
    if (p === path) return true;
    return path.startsWith(p + '/');
  });
}

function inject(cfg, path) {
  removeAllInjected();
  if (!cfg) return;
  if (!currentPathMatches(cfg, path)) return;

  const head = document.head;
  const body = document.body;

  const headParts = [];
  const bodyParts = [];

  const ga4 = ga4Snippet(cfg.ga4_measurement_id);
  if (ga4) headParts.push(...ga4);
  const meta = metaSnippet(cfg.meta_pixel_id);
  if (meta) headParts.push(...meta);
  const li = linkedinSnippet(cfg.linkedin_insight_partner_id);
  if (li) headParts.push(...li);
  const gtm = gtmSnippet(cfg.gtm_container_id);
  if (gtm) headParts.push(...gtm);
  if (cfg.custom_head_html) headParts.push(...parseAndBuild(cfg.custom_head_html, 'custom-head'));
  if (cfg.custom_body_end_html) bodyParts.push(...parseAndBuild(cfg.custom_body_end_html, 'custom-body'));

  // Named multi-snippet list — each entry has {id, label, location, code, enabled}.
  // We fan them out to the right target and give each one a stable
  // ``data-blayzrr-tag`` marker so admin can spot / remove them individually.
  if (Array.isArray(cfg.custom_snippets)) {
    cfg.custom_snippets.forEach((sn) => {
      if (!sn || sn.enabled === false || !sn.code) return;
      const target = sn.location === 'body_end' ? bodyParts : headParts;
      const tagId = `snippet-${(sn.id || '').slice(0, 12) || Math.random().toString(36).slice(2, 10)}`;
      target.push(...parseAndBuild(sn.code, tagId));
    });
  }

  headParts.forEach((el) => { try { head.appendChild(el); } catch (_) { /* ignore */ } });
  bodyParts.forEach((el) => { try { body.appendChild(el); } catch (_) { /* ignore */ } });
}

// Simple module-level cache so we don't re-fetch on every route change.
let _cachedCfg = null;
let _cfgPromise = null;

async function loadConfig() {
  if (_cachedCfg) return _cachedCfg;
  if (!_cfgPromise) {
    _cfgPromise = api.get('/site-config/tracking-scripts')
      .then((r) => { _cachedCfg = r.data; return r.data; })
      .catch(() => { _cachedCfg = null; return null; })
      .finally(() => { _cfgPromise = null; });
  }
  return _cfgPromise;
}

export default function TrackingScriptsInjector() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    loadConfig().then((cfg) => {
      if (cancelled) return;
      inject(cfg, location.pathname);
    });
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Listen for admin's "save" event → invalidate cache + re-inject.
  useEffect(() => {
    const handler = () => {
      _cachedCfg = null;
      loadConfig().then((cfg) => inject(cfg, window.location.pathname));
    };
    window.addEventListener('blayzrr:tracking-scripts-updated', handler);
    return () => window.removeEventListener('blayzrr:tracking-scripts-updated', handler);
  }, []);

  return null;
}
